import { LuiButton, LuiCheckboxInput, LuiIcon, LuiMiniSpinner } from '@linzjs/lui';
import { LuiButtonProps } from '@linzjs/lui/dist/components/LuiButton/LuiButton';
import { IconName } from '@linzjs/lui/dist/components/LuiIcon/LuiIcon';
import clsx from 'clsx';
import { flatMap, omit, pick } from 'lodash-es';
import { PropsWithChildren, ReactElement, useCallback, useMemo, useState } from 'react';

import { debugLog } from '../common/debug';
import { LuiModalAsync, LuiModalAsyncProps } from './LuiModalAsync';
import { LuiModalAsyncButtonGroup } from './LuiModalAsyncButtonGroup';
import { LuiModalAsyncContent } from './LuiModalAsyncContent';
import { LuiModalAsyncCallback, PromiseWithResolve } from './LuiModalAsyncContext';
import { LuiModalAsyncHeader } from './LuiModalAsyncHeader';
import { LuiModalAsyncMain } from './LuiModalAsyncMain';
import { useShowAsyncModal } from './useShowAsyncModal';

export type PrefabType = 'outline' | 'success' | 'info' | 'warning' | 'error' | 'progress' | 'blocked';

export interface LuiModalAsyncPrefabButton<RT> {
  default?: boolean;
  className?: string;
  level?: LuiButtonProps['level'];
  intent?: LuiButtonProps['intent'];
  disabled?: boolean;
  loading?: boolean;
  title: string;
  icon?: IconName;
  iconRight?: IconName;
  value?: RT;
}

export interface useLuiModalPrefabProps<RT = string> extends LuiModalAsyncProps {
  showOnAllWindows?: boolean;
  level: PrefabType;
  title: string;
  helpLink?: string;
  onHelpClick?: () => void;
  buttons?: LuiModalAsyncPrefabButton<RT>[];
  dontShowAgainSessionKey?: string;
  polledCloseCondition?: () => boolean;
}

export interface LuiModalAsyncPrefabProps<RT> extends useLuiModalPrefabProps<RT>, LuiModalAsyncCallback<RT> {
  //
}

// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
export const getIconForLevel = (level: PrefabType): IconName | 'custom_progress' | null => {
  switch (level) {
    case 'success':
      return 'ic_check_circle_outline';
    case 'info':
      return 'ic_info_outline';
    case 'warning':
      return 'ic_warning_outline';
    case 'error':
      return 'ic_error_outline';
    case 'progress':
      return 'custom_progress';
    case 'blocked':
      return 'ic_lock_alt';
    case 'outline':
      return null;
  }
};

const LuiModalDontShowSessionStorageKey = (userKey: string): string => `__LuiModalContext_dontshowagain_${userKey}`;

export const LuiModalDontShowSessionSet = (userKey: string): void =>
  window.sessionStorage.setItem(LuiModalDontShowSessionStorageKey(userKey), 'true');

export const LuiModalDontShowSessionRemove = (userKey: string): void =>
  window.sessionStorage.removeItem(LuiModalDontShowSessionStorageKey(userKey));

export const LuiModalDontShowSessionCheck = (userKey: string): boolean =>
  !!window.sessionStorage.getItem(LuiModalDontShowSessionStorageKey(userKey));

/**
 * A generic template for common types of modals.
 */
export const LuiModalPrefab = (props: PropsWithChildren<LuiModalAsyncPrefabProps<unknown>>): ReactElement => {
  const {
    level = 'warning',
    className,
    style,
    title,
    helpLink,
    onHelpClick,
    children,
    resolve,
    closeOnOverlayClick,
    dontShowAgainSessionKey,
  } = props;
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const icon = getIconForLevel(level);

  /**
   * Default buttons
   * Warning, Info, Error, Success: "Dismiss"
   * Progress: "Cancel"
   */
  const buttons: LuiModalAsyncPrefabButton<unknown>[] = props.buttons ?? [
    {
      title: level === 'progress' ? 'Cancel' : 'Dismiss',
    },
  ];

  // If there are two options, option 1 is secondary, option 2 is primary
  // Otherwise all options are secondary
  if (buttons.length === 2) {
    buttons.forEach((b, i) => {
      if (!b.level) {
        // Last button is primary all others are secondary
        b.level = i === buttons.length - 1 ? 'primary' : 'secondary';
      }
    });
  }

  /**
   * When dialog closes save the "don't show" property
   */
  const setDontShow = () => {
    if (dontShowAgainSessionKey && dontShowAgain) {
      LuiModalDontShowSessionSet(dontShowAgainSessionKey);
    }
  };

  return (
    <LuiModalAsync
      className={clsx('LuiModalPrefab', `LuiModalPrefab-${level}`, className)}
      style={style}
      closeOnOverlayClick={closeOnOverlayClick}
    >
      <LuiModalAsyncMain>
        <LuiModalAsyncHeader
          title={title}
          icon={icon === 'custom_progress' ? <LuiMiniSpinner size={24} /> : icon}
          helpLink={helpLink}
          onHelpClick={onHelpClick}
        />
        <LuiModalAsyncContent>{children}</LuiModalAsyncContent>
        {dontShowAgainSessionKey && (
          <LuiCheckboxInput
            className={'LuiCheckboxInput--nomargin'}
            label={"Don't show me this again"}
            value={'dontShowAgain'}
            isChecked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
          />
        )}
      </LuiModalAsyncMain>
      <LuiModalAsyncButtonGroup>
        {buttons.map((pf, i) => (
          <LuiButton
            key={i}
            level={pf.level ?? 'secondary'}
            intent={pf.intent}
            disabled={pf.disabled}
            loading={pf.loading}
            className={clsx(pf.className, pf.icon && 'lui-button-icon', pf.iconRight && 'lui-button-icon-right')}
            onClick={() => {
              setDontShow();
              resolve(pf.value);
            }}
            buttonProps={pf.default ? { 'data-autofocus': true } : undefined}
          >
            {pf.icon && <LuiIcon name={pf.icon} alt={pf.title} size={'md'} />}
            {pf.title}
            {pf.iconRight && <LuiIcon name={pf.iconRight} alt={pf.title} size={'md'} />}
          </LuiButton>
        ))}
      </LuiModalAsyncButtonGroup>
    </LuiModalAsync>
  );
};

/**
 * Instantiate a generic template for common types of modals.
 *
 * Usage:
 * const { modalOwnerRef, showPrefabModal } = useLuiModalPrefab();
 *
 * return (
 *   <button
 *     ref={modalOwnerRef}
 *       onClick={() =>
 *         showPrefabModal({
 *           level: 'info',
 *           title: 'You are a fantastic person',
 *           children: 'Keep it up!',
 *         })
 *   }>
 *   Open Modal
 *   </button>
 */
export const useLuiModalPrefab = () => {
  const { showModal, modalOwnerRef } = useShowAsyncModal();

  const showPrefabModal = useCallback(
    <RT = string,>(props: PropsWithChildren<useLuiModalPrefabProps<RT>>): PromiseWithResolve<RT> => {
      debugLog(`Show '${props.level}' prefab-modal '${props.title}'`, props);
      if (typeof props.children === 'string') {
        // Convert \n to <br/>
        const split = props.children.split('\n');
        props.children = flatMap(split.map((part, i) => (i !== split.length - 1 ? [part, <br key={i} />] : part)));
      }
      if (
        (props.dontShowAgainSessionKey && LuiModalDontShowSessionCheck(props.dontShowAgainSessionKey)) ||
        (props.polledCloseCondition && props.polledCloseCondition())
      ) {
        // Add resolve method to make the signature of showPrefabModal consistent
        const prom = Promise.resolve(undefined) as PromiseWithResolve<RT>;
        prom.resolve = (_val: RT | undefined) => {};
        return prom;
      }
      return showModal(
        LuiModalPrefab,
        omit(props, ['showOnAllWindows', 'polledCloseCondition']),
        pick(props, ['showOnAllWindows', 'polledCloseCondition']),
      ) as PromiseWithResolve<RT>;
    },
    [showModal],
  );

  return useMemo(
    () => ({
      modalOwnerRef,
      showPrefabModal,
    }),
    [modalOwnerRef, showPrefabModal],
  );
};
